<template>
  <div class="w-full max-w-none px-4 md:px-6 lg:px-8 xl:px-10 mx-auto flex flex-col gap-8 relative">

    <!-- 🔵 Loader -->
    <div
      v-if="isLoading"
      class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50 rounded-xl"
    >
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
    </div>

    <!-- Wizard Header -->
    <div class="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <i class="ri-add-circle-line text-blue-600"></i> Add New Website
        </h1>
        <p class="text-gray-500 text-sm">Follow the guided steps to connect your website.</p>
      </div>
      <div class="flex items-center gap-2">
        <button
         @click="showVideoModal = true"
          class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-blue-700 transition"
        >
          <i class="ri-question-line text-base"></i> Help
        </button>
      </div>
    </div>

    <!-- Message -->
    <div
      v-if="msgTxt"
      :class="`p-3 mt-4 rounded-lg text-sm ${
        msgType === 'success'
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700'
      }`"
    >
      {{ msgTxt }}
    </div>

    <!-- Wizard Layout -->
    <div class="grid lg:grid-cols-3 gap-8">
      <!-- Steps Sidebar -->
      <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Setup Progress</h2>
        <div class="space-y-6">
          <div
            v-for="(s, i) in steps"
            :key="i"
            class="flex items-start gap-3 relative"
          >
            <div
              class="w-9 h-9 flex items-center justify-center font-semibold rounded-full shadow-sm"
              :class="[
                currentStep === i + 1
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-500 text-white shadow-md scale-105'
                  : currentStep > i + 1
                  ? 'bg-green-100 text-green-600'
                  : 'bg-gray-100 text-gray-500'
              ]"
            >
              {{ i + 1 }}
            </div>
            <div>
              <h3
                class="font-medium text-sm"
                :class="currentStep === i + 1 ? 'text-gray-900' : 'text-gray-600'"
              >
                {{ s.title }}
              </h3>
              <p class="text-xs text-gray-400">{{ s.desc }}</p>
            </div>
            <div
              v-if="i < steps.length - 1"
              class="absolute left-4 top-9 w-px h-6 bg-gray-200"
            ></div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
        <!-- Progress Indicator -->
        <div class="relative mb-6">
          <div class="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
            <div
              class="h-1 bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500 ease-out"
              :style="{ width: `${(currentStep / steps.length) * 100}%` }"
            ></div>
          </div>
          <div class="absolute -top-6 right-0 text-xs text-gray-500">
            Step {{ currentStep }} of {{ steps.length }}
          </div>
        </div>

        <!-- Step 1 -->
        <transition name="fade" mode="out-in">
          <div v-if="currentStep === 1" key="step1" class="space-y-6">
            <h2 class="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <i class="ri-global-line text-blue-600"></i> Website Details
            </h2>
            <p class="text-gray-500 text-sm">Enter your website URL and timezone to start tracking.</p>

            <div class="space-y-5 max-w-lg">
              <div>
                <label class="text-sm font-medium text-gray-700">Website Domain</label>
                <input
                  v-model="form.domain"
                  type="text"
                  placeholder="https://example.com"
                  class="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                />
              </div>

              <div>
                <label class="text-sm font-medium text-gray-700">Select Timezone</label>
                <select
                  v-model="form.timezone"
                  class="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm text-gray-600"
                >
                  <option value="">Select your timezone</option>
                  <option v-for="tz in timezones" :key="tz" :value="tz">
                    {{ tz }}
                  </option>
                </select>
              </div>

              <div
                class="bg-gray-50 border border-gray-100 rounded-lg p-4 grid grid-cols-2 gap-2 text-sm text-gray-600"
              >
                <div class="flex items-center gap-2">
                  <i class="ri-checkbox-circle-line text-green-500"></i> Real-time tracking
                </div>
                <div class="flex items-center gap-2">
                  <i class="ri-checkbox-circle-line text-green-500"></i> GDPR compliant
                </div>
                <div class="flex items-center gap-2">
                  <i class="ri-checkbox-circle-line text-green-500"></i> No cookies required
                </div>
                <div class="flex items-center gap-2">
                  <i class="ri-checkbox-circle-line text-green-500"></i> Lightweight script (&lt;1KB)
                </div>
              </div>

              <div class="flex justify-end">
                <button
                  @click="nextStep"
                  class="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition"
                >
                  Continue →
                </button>
              </div>
            </div>
          </div>

          <!-- Step 2 -->
          <div v-else-if="currentStep === 2" key="step2" class="space-y-6">
            <h2 class="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <i class="ri-code-s-slash-line text-blue-600"></i> Install Tracking Script
            </h2>
            <p class="text-gray-500 text-sm">
              Copy and paste the script below into your website’s <code>&lt;head&gt;</code> section.
            </p>

            <div
  class="bg-gray-50 border border-gray-100 rounded-lg p-4 relative overflow-x-auto"
  style="
    position: relative;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    padding: 1rem;
    overflow-x: auto;
    overflow-y: hidden;
    word-wrap: break-word;
    white-space: pre-wrap;
    box-sizing: border-box;
  "
>
  <pre
    class="text-sm text-gray-700"
    style="
      font-size: 0.875rem;
      color: #374151;
      margin: 0;
      padding-right: 4.5rem; /* space for button */
      white-space: pre-wrap;
      word-break: break-all;
      overflow-wrap: break-word;
    "
  >
{{ scriptSnippet }}
  </pre>

  <button
    @click="copyScript"
    class="absolute top-2 right-2 text-xs px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
    style="
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      font-size: 0.75rem;
      padding: 0.4rem 0.75rem;
      background-color: #2563eb;
      color: #fff;
      border: none;
      border-radius: 0.375rem;
      cursor: pointer;
      transition: background-color 0.2s ease;
      white-space: nowrap;
    "
    onmouseover="this.style.backgroundColor='#1d4ed8'"
    onmouseout="this.style.backgroundColor='#2563eb'"
  >
    Copy
  </button>
</div>


            <div class="flex justify-between items-center mt-6">
              <button
                @click="prevStep"
                class="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition"
              >
                ← Back
              </button>
              <button
                @click="verifyInstallation"
                class="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-sm hover:shadow-md transition"
              >
                Verify Installation
              </button>
            </div>
          </div>

          <!-- Step 3 -->
          <div v-else key="step3" class="text-center py-10">
            <div class="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
              <i class="ri-checkbox-circle-line text-4xl text-green-600"></i>
            </div>
            <h2 class="text-2xl font-semibold text-gray-800 mb-2">Congratulations!</h2>
            <p class="text-gray-500 text-sm mb-6">
              Your website <strong>{{ form.domain }}</strong> has been successfully verified and added.
            </p>
            <button
              @click="resetWizard"
              class="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition"
            >
              Add Another Website
            </button>
          </div>
        </transition>
      </div>
    </div>
  </div>
  <transition name="fade">
  <div
    v-if="showVideoModal"
    class="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50"
    @click.self="showVideoModal = false"
  >
    <div
      class="bg-white rounded-2xl shadow-2xl w-[90%] max-w-3xl relative overflow-hidden"
      style="animation: fadeInScale 0.3s ease-out;"
    >
      <!-- Close Button -->
      <button
  @click="showVideoModal = false"
  class="absolute top-3 right-3 rounded-full transition flex items-center justify-center"
  style="
    z-index: 10;
    width: 38px;
    height: 38px;
    background-color: #2563eb; /* blue-600 */
    color: #ffffff;
    border: none;
    cursor: pointer;
    box-shadow: 0 3px 8px rgba(37, 99, 235, 0.4);
    transition: all 0.2s ease;
  "
  onmouseover="this.style.backgroundColor='#1d4ed8'"
  onmouseout="this.style.backgroundColor='#2563eb'"
>
  <i class="ri-close-line" style="font-size: 1.25rem; color: #fff;"></i>
</button>


      <!-- Video Player -->
      <div class="w-full h-0 pb-[56.25%] relative bg-black">
        <iframe
          :src="videoUrl"
          allow="autoplay; encrypted-media"
          allowfullscreen
          frameborder="0"
          class="absolute top-0 left-0 w-full h-full rounded-2xl"
        ></iframe>
      </div>
    </div>
  </div>
</transition>
</template>

<script>
import { addBooking } from "@/apis/bookingpageapis";

export default {
  name: "AddWebsite",
  data() {
    return {
      showVideoModal: false,
      isLoading: false,
      msgTxt: "",
      msgType: "",
      currentStep: 1,
      form: { domain: "", timezone: "" },
      steps: [
        { title: "Website Details", desc: "Enter domain and timezone" },
        { title: "Install Script", desc: "Add tracking code to your site" },
        { title: "Verify & Launch", desc: "Confirm setup completion" },
      ],
      timezones: ["UTC", "Asia/Kolkata", "Europe/London", "America/New_York", "Australia/Sydney"],
      ldata: null,
    };
  },
  computed: {
    scriptSnippet() {
      // build safely to avoid eslint/vue parser issues
      return (
        "<scr" +
        'ipt async defer data-domain="' +
        this.form.domain +
        '" data-api="https://api.analytcly.ai/pls/event" ' +
        'src="https://api.analytcly.ai/pls/script"></scr' +
        "ipt>"
      );
    },
  },
  methods: {
    nextStep() {
      if (!this.form.domain || !this.form.timezone) {
        this.msgType = "danger";
        this.msgTxt = "Please fill out domain and timezone before continuing.";
        return;
      }
      this.msgTxt = "";
      this.currentStep++;
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep--;
    },
    async verifyInstallation() {
      this.msgTxt = "";
      this.msgType = "";
      this.isLoading = true;

      try {
        const response = await addBooking(
          "",
          this.form.domain,
          this.scriptSnippet,
          this.form.timezone,
          this.ldata?.user_id
        );

        if (response.status) {
          this.msgType = "success";
          this.msgTxt = "Website added successfully!";
          setTimeout(() => (this.currentStep = 3), 800);
        } else {
          this.msgType = "danger";
          this.msgTxt = response.message || "Failed to add website.";
        }
      } catch {
        this.msgType = "danger";
        this.msgTxt = "Network error while adding website.";
      } finally {
        this.isLoading = false;
      }
    },
    async copyScript() {
      try {
        await navigator.clipboard.writeText(this.scriptSnippet);
        this.msgType = "success";
        this.msgTxt = "Script copied successfully!";
      } catch {
        this.msgType = "danger";
        this.msgTxt = "Failed to copy script.";
      }
    },
    resetWizard() {
      this.msgTxt = "";
      this.msgType = "";
      this.form = { domain: "", timezone: "" };
      this.currentStep = 1;
    },
  },
  created() {
    const data = sessionStorage.getItem("ldata");
    if (data) this.ldata = JSON.parse(data);
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* === Responsive Enhancements === */
@media (max-width: 1024px) {
  /* collapse 3 cols → 1 col on tablets */
  .grid.lg\:grid-cols-3 {
    display: grid;
    grid-template-columns: 1fr !important;
    gap: 1.5rem !important;
  }

  /* inner card padding */
  .bg-white.rounded-2xl.border.shadow-sm.p-8 {
    padding: 1.5rem !important;
  }

  /* reduce sidebar padding */
  .bg-white.rounded-2xl.shadow-sm.border.border-gray-100.p-6 {
    padding: 1.25rem !important;
  }
}

@media (max-width: 768px) {
  /* stack header vertically */
  .flex.items-center.justify-between.flex-wrap.gap-3 {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.75rem !important;
  }

  /* buttons full width */
  .flex.items-center.gap-2 > button {
    width: 100% !important;
    justify-content: center !important;
  }

  /* adjust outer padding */
  .w-full.max-w-none.px-4.md\:px-6.lg\:px-8.xl\:px-10.mx-auto.flex.flex-col.gap-8.relative {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  /* reduce heading size */
  h1.text-2xl.font-bold {
    font-size: 1.25rem !important;
  }

  /* fix grid items inside cards */
  .grid.grid-cols-2.gap-2.text-sm.text-gray-600 {
    grid-template-columns: 1fr !important;
  }
}

@media (max-width: 480px) {
  /* smallest screens tweaks */
  .bg-white.rounded-2xl.border.shadow-sm.p-8 {
    padding: 1rem !important;
  }

  h1.text-2xl.font-bold {
    font-size: 1.125rem !important;
  }

  p.text-gray-500.text-sm {
    font-size: 0.8125rem !important;
  }

  button,
  input,
  select {
    font-size: 0.875rem !important;
  }
}

/* === FIX: Step 2 Script Box Width Issue === */
.bg-gray-50.border.border-gray-100.rounded-lg.p-4.relative.overflow-x-auto {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  word-wrap: break-word;
  white-space: pre-wrap; /* allow wrapping */
  box-sizing: border-box;
}

.bg-gray-50.border.border-gray-100.rounded-lg.p-4.relative.overflow-x-auto pre {
  white-space: pre-wrap;       /* wrap long script lines */
  word-break: break-all;       /* break very long tokens */
  overflow-wrap: break-word;   /* ensure text stays inside */
  max-width: 100%;
  box-sizing: border-box;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes fadeInScale {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

</style>

